/**
 * Google Banana Pro 图片生成节点 - 专业版图像生成模型
 * 
 * 功能特性：
 * 1. 精准文字生成：准确渲染多语言文字
 * 2. 实时信息融合：连接 Google Search 获取实时信息
 * 3. 多图融合：支持最多14张参考图片
 * 4. 高分辨率输出：支持高达4K分辨率
 * 5. 专业宽高比控制：11种预设比例+跟随参考图
 */
import React, { memo, useState, useEffect, useCallback } from 'react';
import { useReactFlow, useStore, Handle, Position } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { useProject } from '../providers/ProjectProvider';
import { useNodeOperations } from '../providers/NodeOperationsProvider';
import ApiService from '../../../api/ApiService';
import LocalDataService from '../../../api/LocalDataService';
import { toast } from 'react-toastify';
import './BananaProNode.css';

const apiService = new ApiService();

export const BananaProNode = memo(({ id, data }) => {
  const { updateNodeData, getNode, getEdges } = useReactFlow();
  const { addNodesWithEdges, duplicateNode, createNodeAndConnect } = useNodeOperations();
  const edges = useStore((state) => state.edges);
  const nodes = useStore((state) => state.nodes);  // ⚠️ 必须订阅 nodes
  const { readOnly } = useProject();
  
  // 基础设置
  const [aspectRatio, setAspectRatio] = useState(data.aspectRatio || '4:3');
  const [resolution, setResolution] = useState(data.resolution || '2K');
  const [outputFormat, setOutputFormat] = useState(data.outputFormat || 'png');
  // 模型版本选择：standard（标准版）或 cost-effective（性价比版）
  const [modelVersion, setModelVersion] = useState(data.modelVersion || 'standard');

  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  
  // ⚠️ 使用 state 存储连接状态，确保 UI 能正确响应变化
  const [connectedImages, setConnectedImages] = useState([]);
  const [hasPromptConnection, setHasPromptConnection] = useState(false);

  // 同步节点数据
  useEffect(() => {
    updateNodeData(id, {
      aspectRatio,
      resolution,
      outputFormat,
      modelVersion,
    });
  }, [id, aspectRatio, resolution, outputFormat, modelVersion, updateNodeData]);

  // ⚠️ 监听 edges 变化，更新提示词连接状态
  useEffect(() => {
    const connectedEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === 'prompt'
    );
    setHasPromptConnection(connectedEdges.length > 0);
  }, [edges, id]);

  // ⚠️ 监听 edges 和 nodes 变化，更新连接的图片列表
  // 必须同时监听 edges 和 nodes：
  // - edges 变化：连接/断开时触发
  // - nodes 变化：图片上传、任务完成等节点数据更新时触发
  useEffect(() => {
    const connectedEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === 'image'
    );
    
    const images = [];
    connectedEdges.forEach(edge => {
      const sourceNode = getNode(edge.source);
      
      // 从图片参考节点获取
      if (sourceNode?.type === 'imageReference' && sourceNode.data?.imageUrl) {
        images.push(sourceNode.data.imageUrl);
      }
      // 从手绘节点获取（只使用已上传到云端的图片）
      else if (sourceNode?.type === 'drawing' && sourceNode.data?.imageUrl) {
        const url = sourceNode.data.imageUrl;
        if (!url.startsWith('data:') && !url.startsWith('blob:')) {
          images.push(url);
        }
      }
      // 从任务节点获取完成的图片
      else if (sourceNode?.type === 'imageTask' && sourceNode.data?.generatedImages) {
        const generatedImages = sourceNode.data.generatedImages || [];
        generatedImages.forEach(img => {
          const url = typeof img === 'string' ? img : img.url;
          if (url) images.push(url);
        });
      }
      // 从图片展示节点获取
      else if (sourceNode?.type === 'imageDisplay' && sourceNode.data?.imageUrl) {
        images.push(sourceNode.data.imageUrl);
      }
    });
    
    setConnectedImages(images);
  }, [edges, nodes, getNode, id]);  // ⚠️ 必须包含 nodes

  // 获取连接的提示词（用于生成时获取最新数据）
  const getConnectedPrompt = useCallback(() => {
    const connectedEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === 'prompt'
    );
    
    if (connectedEdges.length === 0) return null;
    
    const promptTexts = [];
    connectedEdges.forEach(edge => {
      const sourceNode = getNode(edge.source);
      if (sourceNode?.type === 'promptInput' && sourceNode.data?.prompt) {
        promptTexts.push(sourceNode.data.prompt);
      }
    });
    
    return promptTexts.length > 0 ? promptTexts.join('\n') : null;
  }, [edges, getNode, id]);

  // 获取连接的图片（用于生成时获取最新数据）
  const getConnectedImagesForGeneration = useCallback(() => {
    // 生成时使用 state 中的图片列表
    return connectedImages;
  }, [connectedImages]);

  // 验证连接
  const isValidConnection = useCallback((connection) => {
    // 如果连接到文本输入handle，只允许文本输入节点
    if (connection.targetHandle === 'prompt') {
      const sourceNode = getNode(connection.source);
      return sourceNode?.type === 'promptInput';
    }
    
    // 如果连接到图片输入handle
    if (connection.targetHandle === 'image') {
      const sourceNode = getNode(connection.source);
      const validTypes = ['imageReference', 'drawing', 'imageTask', 'imageDisplay'];
      
      if (!validTypes.includes(sourceNode?.type)) {
        return false;
      }
      
      // 限制图片数量（最多14张）
      const currentEdges = getEdges();
      const imageEdgesCount = currentEdges.filter(
        edge => edge.target === id && edge.targetHandle === 'image'
      ).length;
      
      if (imageEdgesCount >= 14) {
        toast.warning('最多支持14张参考图');
        return false;
      }
      
      return true;
    }
    
    return true;
  }, [id, getNode, getEdges]);

  // 处理 Handle 标签点击 - 自动创建节点并连接
  const handleHandleLabelClick = useCallback((e, handleId, handleType) => {
    e.stopPropagation();  // 阻止事件冒泡
    
    // 只读模式下禁用
    if (readOnly) {
      toast.info('只读模式下无法创建节点');
      return;
    }
    
    // 调用统一的创建方法（会自动从配置读取限制）
    createNodeAndConnect(id, 'bananaPro', handleId, handleType);
  }, [id, readOnly, createNodeAndConnect]);

  // 宽高比转 size 的映射（性价比版需要）
  const aspectRatioToSize = {
    '1:1': '1024x1024',
    '16:9': '1920x1080',
    '9:16': '1080x1920',
  };

  // 生成图片
  const handleGenerate = async () => {
    try {
      setError(null);
      setIsGenerating(true);

      // 1. 获取提示词
      const prompt = getConnectedPrompt();
      if (!prompt) {
        throw new Error('请先连接文本输入节点');
      }

      // 2. 自动检测模式：根据是否有图片连接判断
      const images = getConnectedImagesForGeneration();
      const isImageToImage = images.length > 0;

      // 3. 获取用户 token
      const userData = LocalDataService.load_user_data();
      if (!userData || !userData.token) {
        throw new Error('请先登录');
      }

      // 4. 根据版本构建不同的请求参数
      let inputParams, appName, taskType;

      if (modelVersion === 'cost-effective') {
        // 性价比版：仅支持 prompt, img, size
        inputParams = {
          prompt: prompt,
          size: aspectRatioToSize[aspectRatio] || '1024x1024',
        };
        if (isImageToImage && images.length > 0) {
          inputParams.img = images;
          appName = '7591415992342888494';  // 有参考图
        } else {
          appName = '7591415871924813860';  // 无参考图
        }
        taskType = 'coze_v2_api';
      } else {
        // 标准版：也使用新的 app_name
        inputParams = {
          prompt: prompt,
          size: aspectRatioToSize[aspectRatio] || '1024x1024',
        };
        if (isImageToImage && images.length > 0) {
          inputParams.img = images;
          appName = '7591415992342888494';  // 有参考图
        } else {
          appName = '7591415871924813860';  // 无参考图
        }
        taskType = 'coze_v2_api';
      }

      // 5. 根据任务类型选择不同的 API 接口
      const apiEndpoint = taskType === 'coze_v2_api' 
        ? `${apiService.apiBase}/v2/tasks/create`    // 性价比版
        : `${apiService.apiBase}/fal/tasks/create`;  // 标准版

      console.log('Banana Pro 请求参数:', { modelVersion, appName, taskType, apiEndpoint, inputParams });

      // 6. 调用 API 创建任务
      const response = await apiService.request(
        apiEndpoint,
        'POST',
        {
          token: userData.token,
          user_type: 1,
          app_name: appName,
          task_type: taskType,
          input_params: inputParams,
        }
      );

      console.log('任务创建响应:', response);

      if (response.code === 200 && response.data) {
        const taskId = response.data.task_id;
        toast.success(`Banana Pro 图片任务已创建！${isImageToImage ? `使用 ${images.length} 张参考图` : ''}`);

        // 7. 创建任务节点
        const currentNode = getNode(id);
        const taskNodeId = nanoid();
        const taskNode = {
          id: taskNodeId,
          type: 'imageTask',
          position: {
            x: currentNode.position.x + 450,
            y: currentNode.position.y,
          },
          data: {
            taskId: taskId,
            taskType: modelVersion === 'cost-effective' ? 'banana-pro-cost-effective' : 'banana-pro',
            apiTaskType: taskType,  // 'coze_v2_api' 或 'rep_api'，用于选择正确的 API 端点
            status: 'pending',
            inputParams: inputParams,
            generatedImages: [],
          },
        };

        // 7. 创建连接边
        const newEdge = {
          id: nanoid(),
          source: id,
          target: taskNodeId,
          type: 'default',
        };

        // 8. 添加节点和边
        await addNodesWithEdges([taskNode], [newEdge]);

        setIsGenerating(false);
      } else {
        throw new Error(response.message || '创建任务失败');
      }

    } catch (err) {
      console.error('Banana Pro 图片生成失败:', err);
      const errorMessage = err?.message || '生成失败，请重试';
      setError(errorMessage);
      toast.error(errorMessage);
      setIsGenerating(false);
    }
  };

  const handleDuplicate = (e) => {
    e.stopPropagation();
    duplicateNode(id);
  };

  // 确定当前模式（使用 state 中的数据）
  const isImageToImageMode = connectedImages.length > 0;

  return (
    <div className="BananaProNode-container">
      {/* 复制按钮 */}
      <button
        className="BananaProNode-duplicate-button"
        onClick={handleDuplicate}
        title="复制节点"
      >
        <i className="bi bi-copy"></i>
      </button>

      {/* 文本输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="prompt"
        className="BananaProNode-handle BananaProNode-handle-prompt"
        style={{ top: '30%' }}
        isValidConnection={isValidConnection}
      >
        <div
          className="BananaProNode-handle-label BananaProNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'prompt', 'text')}
          title="点击自动创建文本输入节点"
        >
          文本
        </div>
      </Handle>

      {/* 图片输入 Handle（始终显示） */}
      <Handle
        type="target"
        position={Position.Left}
        id="image"
        className="BananaProNode-handle BananaProNode-handle-image"
        style={{ top: '70%' }}
        isValidConnection={isValidConnection}
      >
        <div
          className="BananaProNode-handle-label BananaProNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'image', 'image')}
          title="点击自动创建图片参考节点（最多14张）"
        >
          参考图
        </div>
      </Handle>

      {/* 输出 Handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="BananaProNode-handle BananaProNode-handle-output"
      />

      {/* 标题栏 */}
      <div className="BananaProNode-title">
        🍌 Banana Pro
        <span className={`BananaProNode-mode-badge ${isImageToImageMode ? 'BananaProNode-mode-i2i' : 'BananaProNode-mode-t2i'}`}>
          {isImageToImageMode ? '图生图' : '文生图'}
        </span>
        {modelVersion === 'cost-effective' && (
          <span className="BananaProNode-mode-badge BananaProNode-mode-cost">
            💰 性价比
          </span>
        )}
      </div>

      <div className="BananaProNode-wrapper">
        {/* 版本选择 */}
        <div className="BananaProNode-field">
          <label className="BananaProNode-label">模型版本</label>
          <select
            value={modelVersion}
            onChange={(e) => setModelVersion(e.target.value)}
            disabled={isGenerating || readOnly}
            className="BananaProNode-select"
          >
            <option value="standard">标准版（完整功能）</option>
            <option value="cost-effective">性价比版（低成本）</option>
          </select>
          <div className="BananaProNode-version-hint">
            {modelVersion === 'standard' 
              ? '支持分辨率、输出格式等完整参数' 
              : '仅支持提示词、参考图、尺寸，成本更低'}
          </div>
        </div>

        {/* 连接状态显示 - 使用 state 确保正确响应变化 */}
        <div className="BananaProNode-status">
          <div className="BananaProNode-status-item">
            <span className="BananaProNode-status-label">文本输入：</span>
            <span className={`BananaProNode-status-value ${hasPromptConnection ? 'BananaProNode-status-connected' : 'BananaProNode-status-disconnected'}`}>
              {hasPromptConnection ? '✓ 已连接' : '✗ 未连接'}
            </span>
          </div>
          <div className="BananaProNode-status-item">
            <span className="BananaProNode-status-label">参考图输入：</span>
            <span className={`BananaProNode-status-value ${connectedImages.length > 0 ? 'BananaProNode-status-connected' : 'BananaProNode-status-disconnected'}`}>
              {connectedImages.length > 0 ? `✓ 已连接 ${connectedImages.length} 张` : '✗ 未连接'}
            </span>
          </div>
        </div>

        {/* 分辨率选择 - 仅标准版显示 */}
        {modelVersion === 'standard' && (
          <div className="BananaProNode-field">
            <label className="BananaProNode-label">分辨率</label>
            <div className="BananaProNode-button-group">
              {['1K', '2K', '4K'].map((res) => (
                <button
                  key={res}
                  onClick={() => setResolution(res)}
                  disabled={isGenerating || readOnly}
                  className={`BananaProNode-button ${resolution === res ? 'BananaProNode-button-active' : ''}`}
                >
                  {res}
                </button>
              ))}
            </div>
            <div className="BananaProNode-resolution-hint">
              {resolution === '1K' && '标准清晰度（免费）'}
              {resolution === '2K' && '高清（需VIP）'}
              {resolution === '4K' && '超清（需SVIP）'}
            </div>
          </div>
        )}

        {/* 宽高比 */}
        <div className="BananaProNode-field">
          <label className="BananaProNode-label">宽高比</label>
          <select
            value={aspectRatio}
            onChange={(e) => setAspectRatio(e.target.value)}
            disabled={isGenerating || readOnly}
            className="BananaProNode-select"
          >
            <option value="1:1">1:1 正方形</option>
            <option value="16:9">16:9 宽屏</option>
            <option value="9:16">9:16 竖屏</option>
            {isImageToImageMode && (
              <option value="match_input_image">跟随主图宽高比</option>
            )}
          </select>
        </div>

        {/* 输出格式 - 仅标准版显示 */}
        {modelVersion === 'standard' && (
          <div className="BananaProNode-field">
            <label className="BananaProNode-label">输出格式</label>
            <div className="BananaProNode-button-group">
              <button
                onClick={() => setOutputFormat('png')}
                disabled={isGenerating || readOnly}
                className={`BananaProNode-button ${outputFormat === 'png' ? 'BananaProNode-button-active' : ''}`}
              >
                PNG
              </button>
              <button
                onClick={() => setOutputFormat('jpg')}
                disabled={isGenerating || readOnly}
                className={`BananaProNode-button ${outputFormat === 'jpg' ? 'BananaProNode-button-active' : ''}`}
              >
                JPG
              </button>
            </div>
          </div>
        )}

        {/* 功能特性展示 */}
        <div className="BananaProNode-features">
          {modelVersion === 'standard' ? (
            <>
              <div className="BananaProNode-feature-item">
                ✨ 精准文字生成（多语言）
              </div>
              <div className="BananaProNode-feature-item">
                🌐 实时信息融合（Google Search）
              </div>
              {isImageToImageMode && (
                <div className="BananaProNode-feature-item">
                  🎨 多图融合：{connectedImages.length}/14 张参考图
                </div>
              )}
              <div className="BananaProNode-feature-item">
                ⏱️ 生成时间：10-25 秒
              </div>
            </>
          ) : (
            <>
              <div className="BananaProNode-feature-item">
                💰 成本更低，适合批量生成
              </div>
              <div className="BananaProNode-feature-item">
                ⚡ 快速生成，简化参数
              </div>
              {isImageToImageMode && (
                <div className="BananaProNode-feature-item">
                  🎨 多图参考：{connectedImages.length} 张
                </div>
              )}
            </>
          )}
        </div>

        {/* 使用说明 */}
        <div className="BananaProNode-hint">
          💡 <strong>文生图模式：</strong>仅连接文本输入节点<br />
          🎨 <strong>图生图模式：</strong>连接文本输入 + 图片参考节点<br />
          {modelVersion === 'standard' 
            ? '📝 支持精准文字渲染和实时信息可视化' 
            : '💰 性价比版成本更低，仅支持基础参数'}<br />
          模式会根据是否连接图片自动切换
        </div>

        {/* 生成按钮 */}
        <button
          onClick={handleGenerate}
          disabled={isGenerating || readOnly}
          className={`BananaProNode-generate ${
            isGenerating || readOnly ? 'BananaProNode-generate-disabled' : ''
          }`}
        >
          {isGenerating ? '创建中...' : '创建任务'}
        </button>

        {/* 错误提示 */}
        {error && (
          <div className="BananaProNode-error">
            ❌ {error}
          </div>
        )}
      </div>
    </div>
  );
});

BananaProNode.displayName = 'BananaProNode';

